import {
  RAIL_STATE_IDS,
  VIDEO_LAYOUT_IDS,
  normalizeRailStateId,
  normalizeVideoLayoutId,
} from '../layouts'
import { LAYOUT_MODES, SURFACE_POLICY_IDS } from './layoutModes'

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function freezePolicy(policy) {
  Object.keys(policy).forEach((key) => {
    if (Array.isArray(policy[key])) {
      Object.freeze(policy[key])
    }
  })

  return Object.freeze(policy)
}

function createPolicy(config) {
  const allowedVideoLayoutIds = normalizeAllowedVideoLayoutIds(
    config?.allowedVideoLayoutIds,
  )
  const allowedRailStateIds = normalizeAllowedRailStateIds(config?.allowedRailStateIds)
  const defaultVideoLayoutId = normalizeOptionalString(
    normalizeVideoLayoutId(
      config?.defaultVideoLayoutId,
      allowedVideoLayoutIds[0] || VIDEO_LAYOUT_IDS.stageOverlay,
    ),
    allowedVideoLayoutIds[0] || VIDEO_LAYOUT_IDS.stageOverlay,
  )
  const defaultRailStateId = normalizeOptionalString(
    normalizeRailStateId(
      config?.defaultRailStateId,
      allowedRailStateIds[0] || RAIL_STATE_IDS.stacked,
    ),
    allowedRailStateIds[0] || RAIL_STATE_IDS.stacked,
  )

  return freezePolicy({
    ...config,
    defaultLayoutMode: defaultVideoLayoutId,
    defaultVideoLayoutId: allowedVideoLayoutIds.includes(defaultVideoLayoutId)
      ? defaultVideoLayoutId
      : allowedVideoLayoutIds[0] || VIDEO_LAYOUT_IDS.stageOverlay,
    allowedVideoLayoutIds,
    defaultRailStateId: allowedRailStateIds.includes(defaultRailStateId)
      ? defaultRailStateId
      : allowedRailStateIds[0] || RAIL_STATE_IDS.stacked,
    allowedRailStateIds,
    allowManualFocus: config?.allowManualFocus !== false,
    allowSelfPreviewPip: config?.allowSelfPreviewPip !== false,
    allowScreenShare: config?.allowScreenShare === true,
    promoteScreenShareToHero: config?.promoteScreenShareToHero !== false,
    allowAudioControl: config?.allowAudioControl !== false,
    allowVideoControl: config?.allowVideoControl !== false,
    allowDeviceSettingsControl: config?.allowDeviceSettingsControl !== false,
    showLocalParticipantInLayout:
      typeof config?.showLocalParticipantInLayout === 'boolean'
        ? config.showLocalParticipantInLayout
        : config?.showLocalParticipantInRoster !== false,
  })
}

function getRoleWeight(roleOrder, role) {
  const normalizedRole = normalizeOptionalString(role, '')
  const matchedIndex = roleOrder.indexOf(normalizedRole)

  return matchedIndex === -1 ? roleOrder.length : matchedIndex
}

function compareDisplayName(leftParticipant, rightParticipant) {
  const leftDisplayName = normalizeOptionalString(leftParticipant?.displayName, '')
  const rightDisplayName = normalizeOptionalString(rightParticipant?.displayName, '')

  return leftDisplayName.localeCompare(rightDisplayName)
}

const ALL_VIDEO_LAYOUT_IDS = Object.freeze([
  VIDEO_LAYOUT_IDS.stageOverlay,
  VIDEO_LAYOUT_IDS.split,
  VIDEO_LAYOUT_IDS.grid,
])

const ALL_RAIL_STATE_IDS = Object.freeze([
  RAIL_STATE_IDS.collapsed,
  RAIL_STATE_IDS.docked,
  RAIL_STATE_IDS.stacked,
  RAIL_STATE_IDS.filmstrip,
])

function normalizeAllowedVideoLayoutIds(layoutIds = []) {
  const normalizedLayoutIds = Array.isArray(layoutIds)
    ? layoutIds
        .map((layoutId) => normalizeVideoLayoutId(layoutId, null))
        .filter((layoutId) => ALL_VIDEO_LAYOUT_IDS.includes(layoutId))
    : []

  return normalizedLayoutIds.length
    ? Object.freeze(Array.from(new Set(normalizedLayoutIds)))
    : ALL_VIDEO_LAYOUT_IDS
}

function normalizeAllowedRailStateIds(railStateIds = []) {
  const normalizedRailStateIds = Array.isArray(railStateIds)
    ? railStateIds
        .map((railStateId) => normalizeRailStateId(railStateId, null))
        .filter((railStateId) => ALL_RAIL_STATE_IDS.includes(railStateId))
    : []

  return normalizedRailStateIds.length
    ? Object.freeze(Array.from(new Set(normalizedRailStateIds)))
    : ALL_RAIL_STATE_IDS
}

const CHAMBER_FAMILY_POLICY = Object.freeze({
  defaultLayoutMode: LAYOUT_MODES.stageOverlay,
  defaultVideoLayoutId: VIDEO_LAYOUT_IDS.stageOverlay,
  allowedVideoLayoutIds: ALL_VIDEO_LAYOUT_IDS,
  defaultRailStateId: RAIL_STATE_IDS.stacked,
  allowedRailStateIds: ALL_RAIL_STATE_IDS,
  controlsOverlayMode: 'overlay',
  useFullVideoRegion: true,
  includeParticipantsWithoutMedia: true,
  includeParticipantsWithoutMetadata: true,
  allowManualFocus: true,
  allowSelfPreviewPip: true,
  allowScreenShare: true,
  promoteScreenShareToHero: true,
  showLocalParticipantInRoster: true,
  localParticipantEligibleForHero: false,
  hiddenRoles: ['auditor', 'panelist', 'observer'],
  rosterRoleOrder: ['patient', 'mentor', 'doctor', 'panelist', 'auditor'],
  heroRoleOrder: ['patient', 'mentor', 'doctor', 'panelist', 'auditor'],
  heroExcludedRoles: ['auditor', 'panelist', 'observer'],
})

export const SURFACE_POLICIES = Object.freeze({
  [SURFACE_POLICY_IDS.mentorChamber]: createPolicy({
    id: SURFACE_POLICY_IDS.mentorChamber,
    ...CHAMBER_FAMILY_POLICY,
  }),
  [SURFACE_POLICY_IDS.kioskScreen]: createPolicy({
    id: SURFACE_POLICY_IDS.kioskScreen,
    ...CHAMBER_FAMILY_POLICY,
    showLocalParticipantInRoster: false,
    showLocalParticipantInLayout: true,
  }),
  [SURFACE_POLICY_IDS.standardLive]: createPolicy({
    id: SURFACE_POLICY_IDS.standardLive,
    defaultLayoutMode: LAYOUT_MODES.stageOverlay,
    defaultVideoLayoutId: VIDEO_LAYOUT_IDS.stageOverlay,
    allowedVideoLayoutIds: ALL_VIDEO_LAYOUT_IDS,
    defaultRailStateId: RAIL_STATE_IDS.stacked,
    allowedRailStateIds: ALL_RAIL_STATE_IDS,
    controlsOverlayMode: 'overlay',
    useFullVideoRegion: true,
    includeParticipantsWithoutMedia: true,
    includeParticipantsWithoutMetadata: true,
    allowManualFocus: true,
    allowSelfPreviewPip: true,
    allowScreenShare: true,
    showLocalParticipantInRoster: true,
    localParticipantEligibleForHero: false,
    // Audit viewers join covertly (Agora subscribers never publish; other
    // providers surface them as roster peers) so hide them from participants.
    hiddenRoles: ['auditor'],
    rosterRoleOrder: ['mentor', 'doctor', 'patient', 'user', 'panelist', 'auditor'],
    heroRoleOrder: ['mentor', 'doctor', 'patient', 'user', 'panelist', 'auditor'],
    heroExcludedRoles: ['auditor'],
  }),
  [SURFACE_POLICY_IDS.publicLive]: createPolicy({
    id: SURFACE_POLICY_IDS.publicLive,
    defaultLayoutMode: LAYOUT_MODES.stageOverlay,
    defaultVideoLayoutId: VIDEO_LAYOUT_IDS.stageOverlay,
    allowedVideoLayoutIds: ALL_VIDEO_LAYOUT_IDS,
    defaultRailStateId: RAIL_STATE_IDS.stacked,
    allowedRailStateIds: ALL_RAIL_STATE_IDS,
    controlsOverlayMode: 'overlay',
    useFullVideoRegion: true,
    includeParticipantsWithoutMedia: true,
    includeParticipantsWithoutMetadata: true,
    allowManualFocus: true,
    allowSelfPreviewPip: true,
    allowScreenShare: false,
    showLocalParticipantInRoster: true,
    localParticipantEligibleForHero: false,
    hiddenRoles: ['auditor'],
    rosterRoleOrder: ['mentor', 'doctor', 'patient', 'user', 'panelist', 'auditor'],
    heroRoleOrder: ['mentor', 'doctor', 'patient', 'user', 'panelist', 'auditor'],
    heroExcludedRoles: ['auditor'],
  }),
  [SURFACE_POLICY_IDS.auditObserver]: createPolicy({
    id: SURFACE_POLICY_IDS.auditObserver,
    defaultLayoutMode: LAYOUT_MODES.split,
    defaultVideoLayoutId: VIDEO_LAYOUT_IDS.split,
    allowedVideoLayoutIds: ALL_VIDEO_LAYOUT_IDS,
    defaultRailStateId: RAIL_STATE_IDS.stacked,
    allowedRailStateIds: ALL_RAIL_STATE_IDS,
    controlsOverlayMode: 'overlay',
    useFullVideoRegion: true,
    includeParticipantsWithoutMedia: true,
    includeParticipantsWithoutMetadata: true,
    allowManualFocus: true,
    allowSelfPreviewPip: false,
    allowScreenShare: false,
    promoteScreenShareToHero: false,
    showLocalParticipantInRoster: true,
    localParticipantEligibleForHero: false,
    hiddenRoles: [],
    rosterRoleOrder: ['mentor', 'doctor', 'patient', 'user', 'panelist', 'auditor'],
    heroRoleOrder: ['mentor', 'doctor', 'patient', 'user', 'panelist', 'auditor'],
    heroExcludedRoles: ['auditor', 'observer'],
  }),
  [SURFACE_POLICY_IDS.supportAuditObserver]: createPolicy({
    id: SURFACE_POLICY_IDS.supportAuditObserver,
    defaultLayoutMode: LAYOUT_MODES.split,
    defaultVideoLayoutId: VIDEO_LAYOUT_IDS.split,
    allowedVideoLayoutIds: [VIDEO_LAYOUT_IDS.split, VIDEO_LAYOUT_IDS.grid],
    defaultRailStateId: RAIL_STATE_IDS.stacked,
    allowedRailStateIds: ALL_RAIL_STATE_IDS,
    controlsOverlayMode: 'overlay',
    useFullVideoRegion: true,
    includeParticipantsWithoutMedia: true,
    includeParticipantsWithoutMetadata: true,
    allowManualFocus: true,
    allowSelfPreviewPip: false,
    allowScreenShare: false,
    promoteScreenShareToHero: false,
    allowAudioControl: false,
    allowVideoControl: false,
    allowDeviceSettingsControl: false,
    showLocalParticipantInRoster: false,
    localParticipantEligibleForHero: false,
    hiddenRoles: [],
    rosterRoleOrder: ['mentor', 'doctor', 'patient', 'user', 'panelist', 'auditor'],
    heroRoleOrder: ['mentor', 'doctor', 'patient', 'user', 'panelist', 'auditor'],
    heroExcludedRoles: ['auditor', 'observer'],
  }),
})

/**
 * @param {string | null | undefined} policyId
 * @returns {object}
 */
export function getSurfacePolicy(policyId) {
  return (
    SURFACE_POLICIES[policyId] ||
    SURFACE_POLICIES[SURFACE_POLICY_IDS.standardLive]
  )
}

/**
 * @returns {object[]}
 */
export function listSurfacePolicies() {
  return Object.values(SURFACE_POLICIES)
}

export function getAllowedVideoLayoutIdsForSurface(policyId) {
  return [...getSurfacePolicy(policyId).allowedVideoLayoutIds]
}

export function getDefaultVideoLayoutIdForSurface(policyId) {
  return getSurfacePolicy(policyId).defaultVideoLayoutId
}

export function getAllowedRailStateIdsForSurface(policyId) {
  return [...getSurfacePolicy(policyId).allowedRailStateIds]
}

export function getDefaultRailStateIdForSurface(policyId) {
  return getSurfacePolicy(policyId).defaultRailStateId
}

export function isManualFocusAllowedForSurface(policyId) {
  return getSurfacePolicy(policyId).allowManualFocus !== false
}

export function isSelfPreviewPipAllowedForSurface(policyId) {
  return getSurfacePolicy(policyId).allowSelfPreviewPip !== false
}

/**
 * @param {string | null | undefined} policyId
 * @param {object} participant
 * @returns {boolean}
 */
export function isParticipantVisibleInSurface(policyId, participant) {
  const policy = getSurfacePolicy(policyId)
  const role = normalizeOptionalString(participant?.role, '')

  if (!participant || participant?.connectionState === 'left') {
    return false
  }

  if (!policy.showLocalParticipantInRoster && participant?.isLocal) {
    return false
  }

  return !policy.hiddenRoles.includes(role)
}

function compareParticipantsForRoster(policy, leftParticipant, rightParticipant) {
  const leftRoleWeight = getRoleWeight(policy.rosterRoleOrder, leftParticipant?.role)
  const rightRoleWeight = getRoleWeight(policy.rosterRoleOrder, rightParticipant?.role)

  if (leftRoleWeight !== rightRoleWeight) {
    return leftRoleWeight - rightRoleWeight
  }

  if (leftParticipant?.isLocal !== rightParticipant?.isLocal) {
    return leftParticipant?.isLocal ? 1 : -1
  }

  return compareDisplayName(leftParticipant, rightParticipant)
}

function isParticipantEligibleForHero(policy, participant) {
  const role = normalizeOptionalString(participant?.role, '')

  if (!participant || participant?.connectionState === 'left') {
    return false
  }

  if (!policy.localParticipantEligibleForHero && participant?.isLocal) {
    return false
  }

  return !policy.heroExcludedRoles.includes(role)
}

function compareParticipantsForHero(policy, leftParticipant, rightParticipant) {
  if (
    Boolean(leftParticipant?.screenSharing) !== Boolean(rightParticipant?.screenSharing)
  ) {
    return leftParticipant?.screenSharing ? -1 : 1
  }

  const leftRoleWeight = getRoleWeight(policy.heroRoleOrder, leftParticipant?.role)
  const rightRoleWeight = getRoleWeight(policy.heroRoleOrder, rightParticipant?.role)

  if (leftRoleWeight !== rightRoleWeight) {
    return leftRoleWeight - rightRoleWeight
  }

  return compareParticipantsForRoster(policy, leftParticipant, rightParticipant)
}

/**
 * @param {string | null | undefined} policyId
 * @param {object[]} [participants=[]]
 * @returns {object[]}
 */
export function getVisibleParticipantsForSurface(policyId, participants = []) {
  const policy = getSurfacePolicy(policyId)

  return [...participants]
    .filter((participant) => isParticipantVisibleInSurface(policy.id, participant))
    .sort((leftParticipant, rightParticipant) =>
      compareParticipantsForRoster(policy, leftParticipant, rightParticipant),
    )
}

/**
 * @param {string | null | undefined} policyId
 * @param {object[]} [participants=[]]
 * @param {string | null | undefined} preferredParticipantId
 * @returns {object | null}
 */
export function pickDominantParticipantForSurface(
  policyId,
  participants = [],
  preferredParticipantId,
) {
  const policy = getSurfacePolicy(policyId)
  const visibleParticipants = getVisibleParticipantsForSurface(policy.id, participants)
  const preferredCandidate = visibleParticipants.find(
    (participant) =>
      participant?.id === preferredParticipantId &&
      isParticipantEligibleForHero(policy, participant),
  )

  if (preferredCandidate) {
    return preferredCandidate
  }

  const heroCandidates = visibleParticipants.filter((participant) =>
    isParticipantEligibleForHero(policy, participant),
  )

  if (!heroCandidates.length) {
    return visibleParticipants[0] || null
  }

  heroCandidates.sort((leftParticipant, rightParticipant) =>
    compareParticipantsForHero(policy, leftParticipant, rightParticipant),
  )

  return heroCandidates[0] || null
}
